import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	Signal,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ISbStoryData } from '@storyblok/js';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { MarkdownModule } from 'ngx-markdown';
import { combineLatest, from, map, Observable, switchMap, tap } from 'rxjs';

import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import { CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import {
	fallbackLanguage,
	LANGUAGE,
	Language,
	negativeHeader$,
} from '@valk-nx/core/lib/core';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { PriceHelper } from '@valk-nx/helpers/lib/price/price.helper';
import { ReferenceComponent } from '@valk-nx/storyblok/components/reference/src/lib/reference.component';
import { StoryblokSearchableListSectionComponent } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section';
import { ExtendedPersuadeCardInputs } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section.interface';
import { SectionComponent } from '@valk-nx/storyblok/components/section/src/lib/section';
import { SheetComponent } from '@valk-nx/storyblok/components/sheet/src/lib/sheet';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { removeTrailingSlash } from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import {
	DealData,
	SimpleHotelDealData,
} from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import {
	ISbComponentType,
	ISbStoryBlok,
	TranslatedSlug,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

const Components = {
	reference: ReferenceComponent,
	section: SectionComponent,
};

@Component({
	selector: 'sb-deal-detail',
	templateUrl: './deal-detail.component.html',
	imports: [
		CarouselComponent,
		CommonModule,
		DynamicModule,
		MarkdownModule,
		PersuadeCardComponent,
		StoryblokSearchableListSectionComponent,
		SheetComponent,
		TranslatePipe,
	],
})
export class DealDetailComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly sbService = inject(StoryblokService);
	private readonly metadataService = inject(MetadataService);
	private readonly router = inject(Router);
	private readonly translate = inject(TranslateService);
	private readonly rootLanguage = inject<Language>(LANGUAGE);
	private readonly bookingtoolUrl = inject(BOOKINGTOOL);

	content = input<ISbStoryBlok[]>();

	components = Components;
	currentUrl = signal('');
	deal$: Observable<DealData>;
	images: CarouselItem[] = [];

	deal = signal<DealData | undefined>(undefined);
	hotels = signal<SimpleHotelDealData[]>([]);

	language: string = fallbackLanguage;
	currentLanguage: string = fallbackLanguage;

	readonly dealStatusText = computed(() => {
		const dealData = this.deal();

		if (dealData.isAvailableSoon) {
			return this.translate.instant(
				'storyblok.pages.deal.status.deal-is-future',
			);
		} else if (dealData.isExpired) {
			return this.translate.instant(
				'storyblok.pages.deal.status.deal-is-expired',
			);
		}

		return this.translate.instant(
			'storyblok.pages.deal.status.deal-is-unavailable',
		);
	});

	readonly availableDealHotels: Signal<ExtendedPersuadeCardInputs[]> =
		computed(() => {
			if (this.hotels().length === 0 || this.deal() === undefined)
				return [];

			const dealData = this.deal();

			const mappedAvailableDealHotels = this.hotels().map((hotel) => {
				const hotelAvailable =
					dealData.isAvailableSoon || dealData.isExpired
						? false
						: hotel.isAvailable;

				const bookingUrl = BookingRedirectHelper.getRedirectUrl(
					this.bookingtoolUrl,
					hotel.slug,
					'',
					dealData.guid,
					this.currentLanguage,
				);

				return {
					cardUrl: `${this.currentUrl()}/${hotel.slug}`,
					content: this.arrayToMarkDownList(hotel.usps),
					ctaText: this.translate.instant('global.book-directly'),
					ctaUrl: hotelAvailable ? bookingUrl : null,
					imageRibbon: dealData.ribbonText,
					imageVariant: 'single' as const,
					// TODO: VPL-38836 Fix only passing on 1 image
					images: [hotel.images[0]],
					price: hotelAvailable ? hotel.price : null,
					pricingToolTip: {
						occupancy: PriceHelper.getPricingTooltip(
							dealData.occupancyRestrictions,
						),
					},
					socialProof: hotel.rating,
					title: hotel.name,
					isTransparentImage: !hotelAvailable,
					secondaryCtaText: this.translate.instant(
						'storyblok.pages.deal-overview.view-deal',
					),
					showDisabledText: !hotelAvailable,
					ctaTarget: '_blank' as const,
					secondaryCtaTarget: '_blank' as const,
				};
			});

			return mappedAvailableDealHotels;
		});
	readonly contentInputs = computed<(InputsType & { component: string })[]>(
		() =>
			this.content()?.map((blok) => ({
				...blok,
				component: blok?.['component'] ?? '',
			})) ?? [],
	);

	listTitle = [
		{
			_uid: '',
			size: 'md',
			title: this.translate.instant(
				'storyblok.pages.deal-overview.available-hotels',
			),
			component: 'runningHead',
		},
	];

	constructor() {
		super();

		this.language = this.rootLanguage;
		this.currentLanguage =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();

		this.deal$ = this.sbService.translatedSlugs$.pipe(
			takeUntilDestroyed(),
			map((translatedSlugs) => this.extractPathInfo(translatedSlugs)),
			switchMap(({ path, language }) =>
				combineLatest([
					from(
						this.sbService.getStoriesByQuery(
							{
								starts_with: path,
								excluding_slugs: path,
								per_page: 100,
							},
							language,
						),
					),
					this.contentService.getDeal(
						path.split('/').pop(),
						language,
					),
				]),
			),
			tap(([storiesResponse, dealResponse]) =>
				this.handleSideEffects(
					storiesResponse.data.stories,
					dealResponse.data,
				),
			),
			map(([_, dealResponse]) => dealResponse.data),
		);
	}

	private extractPathInfo(translatedSlugs: TranslatedSlug[]): {
		path: string;
		language: Language;
	} {
		const urlTree = this.router.parseUrl(this.router.url);
		this.currentUrl.set(
			removeTrailingSlash(
				urlTree.root.children['primary']?.segments
					.map((s) => s.path)
					.join('/') ?? '',
			),
		);

		const selectedSlug =
			translatedSlugs.find((x) => x.selected) ?? translatedSlugs[0];
		const path =
			selectedSlug.lang.toLowerCase() !== this.rootLanguage
				? selectedSlug.path.substring(3)
				: selectedSlug.path;

		return {
			path: removeTrailingSlash(path),
			language: selectedSlug.lang.toLowerCase() as Language,
		};
	}

	private handleSideEffects(
		storyblokHotels: ISbStoryData<
			ISbComponentType<string> & Record<string, unknown>
		>[],
		dealData: DealData,
	) {
		this.metadataService.updateMetaDataBatch(
			dealData,
			this.robotsNoFollow,
			this.robotsNoIndex,
		);

		this.images =
			dealData.images?.length > 0
				? dealData.images.map((image) => ({
						image: {
							src: image.src,
							alt: image.alt,
						},
						heading: dealData.name,
						subHeading: this.translate.instant('global.at-valk'),
						sticker: {
							src: dealData.sticker?.src,
							altText: dealData.sticker?.alt,
						},
						button: null,
						link: null,
					}))
				: [];

		if (this.images.length > 0) {
			negativeHeader$.next(true);
		}

		const availableDeals = dealData.hotels.filter((availableHotel) =>
			storyblokHotels.some(
				(storyblokHotel) =>
					storyblokHotel.full_slug.split('/').pop() ===
					availableHotel.slug,
			),
		);

		this.deal.set(dealData);
		this.hotels.set(availableDeals);
	}

	private arrayToMarkDownList(texts: string[] = []): string {
		if (texts.length === 0) return '';

		return texts.map((text) => `- ${text}`).join('\n');
	}
}
